import type { CoviChatItem } from "../model/types"
import { sanitizeHydratedItems } from "./storageValidation"
import { boundTranscriptToolSteps } from "./toolTraceBounds"

/**
 * Client-side persistence for the Sofia panel — one chat transcript per thread,
 * keyed by org slug. Survives reloads and navigations within the same browser.
 * Durable org/user memory lives server-side with the Sofia server
 * (sofia-agent); this is only the panel's local transcript cache.
 *
 * Which threads exist, and what they're called, lives in `storageThreads.ts`.
 */

// v1 was the original proof-of-concept shape (org memory + action/drive items). Bumping
// discards those snapshots instead of hydrating dead item kinds.
// v2 gained the "action" item kind WITHOUT a bump: the addition is
// additive and the previous reader degrades safely (unknown kinds render as
// nothing, are skipped by history building and transcript copy), so existing
// transcripts survive in both directions. Hydration sanitizes action items
// below instead.
const VERSION = 2

interface StoredOrgState {
  version: number
  items: CoviChatItem[]
}

const EMPTY_STATE: StoredOrgState = {
  version: VERSION,
  items: [],
}

/**
 * Per-thread transcript key. The legacy single-transcript key
 * (`coval:copilot:{orgSlug}`, no thread suffix) predates threads entirely and
 * is adopted into thread "0" on first load — see `adoptLegacyTranscript` in
 * `storageThreads.ts`. Every thread otherwise gets its own suffixed key so
 * concurrent threads never overwrite each other.
 */
function storageKey(orgSlug: string, threadId?: string): string {
  const base = `coval:copilot:${orgSlug}`
  return threadId ? `${base}:thread:${threadId}` : base
}

function safeParse(raw: string | null): StoredOrgState | null {
  if (!raw) {
    return null
  }
  try {
    const parsed: unknown = JSON.parse(raw)
    if (!parsed || typeof parsed !== "object") {
      return null
    }
    const candidate = parsed as Partial<StoredOrgState>
    if (candidate.version !== VERSION) {
      return null
    }
    return {
      version: VERSION,
      items: Array.isArray(candidate.items)
        ? sanitizeHydratedItems(candidate.items)
        : [],
    }
  } catch {
    return null
  }
}

export function loadOrgState(
  orgSlug: string | null | undefined,
  threadId?: string,
): StoredOrgState {
  if (!orgSlug || typeof window === "undefined") {
    return { ...EMPTY_STATE, items: [] }
  }
  return (
    safeParse(window.localStorage.getItem(storageKey(orgSlug, threadId))) ?? {
      ...EMPTY_STATE,
      items: [],
    }
  )
}

/**
 * Persist one thread's transcript. Returns `true` when the write reached
 * localStorage and `false` on a swallowed failure (quota exceeded / private
 * mode). Callers that trade one durable copy for another — e.g. migrating an
 * archived conversation into its own thread slot — use the return value to
 * avoid dropping the source copy before the replacement is safely on disk.
 */
export function saveOrgState(
  orgSlug: string | null | undefined,
  state: { items: CoviChatItem[] },
  threadId?: string,
): boolean {
  if (!orgSlug || typeof window === "undefined") {
    return false
  }
  try {
    window.localStorage.setItem(
      storageKey(orgSlug, threadId),
      JSON.stringify({
        version: VERSION,
        ...state,
        items: boundTranscriptToolSteps(state.items),
      }),
    )
    return true
  } catch {
    // Quota exceeded / private mode — drop silently; Sofia still works in-memory.
    return false
  }
}

/**
 * Delete one thread's transcript slot — when its thread is deleted, or when it
 * is pruned to stay inside the storage cap. Omitting `threadId` targets the
 * pre-thread legacy slot, mirroring `loadOrgState`/`saveOrgState`.
 */
export function deleteOrgThreadState(
  orgSlug: string | null | undefined,
  threadId?: string,
): void {
  if (!orgSlug || typeof window === "undefined") {
    return
  }
  try {
    window.localStorage.removeItem(storageKey(orgSlug, threadId))
  } catch {
    // Storage failures are non-fatal — the slot just lingers until quota reclaims it.
  }
}

/**
 * Docked-panel width, in pixels, persisted per browser (NOT per org — it's a
 * layout preference, not chat state). The context clamps the stored value to
 * the current viewport on load, so a width saved on a wide monitor never opens
 * wider than the screen on a laptop.
 */
export const PANEL_WIDTH_STORAGE_KEY = "coval:copilot:panel-width"
/**
 * MIN is also the panel's opening width: it takes as little of the page as it
 * can and widens only when the user drags it there, at which point the stored
 * width takes over on every later open.
 */
export const MIN_PANEL_WIDTH = 360
/** The docked panel never grows past this fraction of the viewport. */
export const MAX_PANEL_WIDTH_FRACTION = 0.66

export function loadPanelWidth(): number | null {
  if (typeof window === "undefined") {
    return null
  }
  try {
    const raw = window.localStorage.getItem(PANEL_WIDTH_STORAGE_KEY)
    if (!raw) {
      return null
    }
    const value = Number(raw)
    return Number.isFinite(value) && value > 0 ? value : null
  } catch {
    return null
  }
}

export function savePanelWidth(width: number): void {
  if (typeof window === "undefined") {
    return
  }
  try {
    window.localStorage.setItem(
      PANEL_WIDTH_STORAGE_KEY,
      String(Math.round(width)),
    )
  } catch {
    // Quota exceeded / private mode — drop silently; width stays in-memory.
  }
}
